/**
 * Módulo `/usage` — ¿el producto se está usando?
 *
 * Es la señal que al panel le faltaba: Revenue dice si se vende, Clientes dice si
 * está instalado y ninguno dice si está **corriendo**. La pantalla cierra esa
 * cadena en tres tramos — contratado → instalado → activo — y ordena todo lo
 * demás por urgencia, igual que el Overview: lo roto arriba, lo que se mueve al
 * medio, lo sano colapsado.
 *
 * **El rol no es un parámetro del front.** Viene discriminado en el payload
 * (`rol`), que el servidor arma según el usuario autenticado y ya recortado con
 * `stripMoney`. Este archivo no filtra un solo campo: lo único que hace con el
 * rol es **no montar** el bloque de margen, que para ops y comercial ni viaja.
 * Si mañana el servidor mandara `economiaPorCuenta` a ops, TypeScript no dejaría
 * leerlo desde la rama de ops, y el test de plata lo cazaría igual.
 *
 * La otra excepción de rol está en la columna de la suscripción de Stripe del
 * bloque de créditos, y su motivo está documentado ahí: para ops la celda solo
 * podría decir "sin vincular" sobre una suscripción que sí existe.
 */
import type { Agent, CreditBalance, UsageClientRow } from '@ops/contracts'
import { formatMetricValue, formatPeriodo } from '@ops/contracts'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { plural } from '../../lib/format'
import { useUsage } from '../../lib/queries'
import { irA } from '../../lib/scroll'
import { usePeriodo } from '../../shell'
import {
  Button,
  Section,
  Stack,
  TriageBar,
  type TriageConteo,
} from '../../ui'
import { Cargando, CrearSeguimientoDialog, ErrorDeCarga, Vacio } from '../comunes'
import { AgentesBlock } from './AgentesBlock'
import { CadenaBlock } from './CadenaBlock'
import { ConsumoMensual } from './ConsumoMensual'
import { CreditosBlock } from './CreditosBlock'
import { CuentasEnCaida } from './CuentasEnCaida'
import { CuentasSanas } from './CuentasSanas'
import { EconomiaBlock } from './EconomiaBlock'
import { PorModelo } from './PorModelo'
import { TablaCuentas } from './TablaCuentas'
import { UsageKpiCard } from './UsageKpiCard'
import { conteosUsage, titularUsage } from './derivar'

/** Anclas de las zonas y de los bloques a los que baja la barra de triage. */
const ZONA_1 = 'usage-zona-1'
const SIN_USO = 'usage-agentes-sin-uso'
const SIN_MEDICION = 'usage-agentes-sin-medicion'
const CAIDA = 'usage-cuentas-en-caida'
const CREDITOS = 'usage-creditos'
const CADENA = 'usage-cadena'

/** Sobre qué se abre el diálogo de seguimiento. Siempre una cuenta: ver abajo. */
interface Objetivo {
  clientId: string
  clientNombre: string
  sugerencia: string
  contexto: string
}

export interface UsagePageProps {
  /** Reloj de la pantalla. Inyectable para que todos los badges de frescura
   *  coincidan en un render, y para los tests. */
  now?: Date
}

export default function UsagePage({ now: nowProp }: UsagePageProps = {}) {
  const { periodo } = usePeriodo()
  const navegar = useNavigate()
  const [ahoraLocal] = useState(() => new Date())
  const now = nowProp ?? ahoraLocal
  const consulta = useUsage(periodo)

  const [objetivo, setObjetivo] = useState<Objetivo | null>(null)

  if (consulta.isPending) {
    return (
      <Section
        titulo="Cómo se está usando el producto"
        bajada={`Trayendo el consumo de ${formatPeriodo(periodo)}.`}
        regla={false}
      >
        <Cargando
          que={`el consumo de ${formatPeriodo(periodo)}`}
          alto="lg"
          testId="usage-cargando"
        />
      </Section>
    )
  }

  if (consulta.isError) {
    return (
      <Section
        titulo="No pudimos abrir el consumo"
        bajada="La pantalla no muestra nada hasta saber qué pasó: media medición se lee como una medición."
        regla={false}
      >
        <ErrorDeCarga
          que={`el consumo de ${formatPeriodo(periodo)}`}
          error={consulta.error}
          onRetry={() => void consulta.refetch()}
          reintentando={consulta.isFetching}
          testId="usage-error-mensaje"
        />
      </Section>
    )
  }

  const payload = consulta.data
  const esAdmin = payload.rol === 'admin'

  const conteos = conteosUsage(payload)
  const veredicto = titularUsage(conteos)

  /*
   * El seguimiento se abre **sobre la cuenta, no sobre el agente ni el saldo.**
   * `EntityType` del contrato tiene tres valores (`client`, `metric`,
   * `activation`) y ninguno es "agente" ni "crédito": crear el work item contra
   * un id que ninguna entidad del panel declara dejaría un seguimiento que
   * después nadie puede volver a encontrar desde la ficha. El título y el
   * contexto nombran lo concreto, que es lo que hace falta para trabajarlo.
   */
  const seguirAgente = (agente: Agent) =>
    setObjetivo({
      clientId: agente.clientId,
      clientNombre: agente.clientNombre,
      sugerencia: `Poner a producir ${agente.nombre} en ${agente.clientNombre}`,
      contexto:
        agente.consumo === null
          ? `${agente.nombre} (${agente.rolFuncional}) está desplegado en ${agente.clientNombre} y no se puede medir: la plataforma no le estampó el prefijo de sesión de Langfuse, así que su consumo es desconocido y no cero.`
          : `${agente.nombre} (${agente.rolFuncional}) está desplegado en ${agente.clientNombre} con cero trazas en ${formatPeriodo(payload.periodo)}${
              agente.diasSinUso === null
                ? '.'
                : `, y lleva ${formatMetricValue(agente.diasSinUso, 'count')} ${plural(agente.diasSinUso, 'día', 'días')} sin actividad.`
            } La cuenta paga por él y no produce.`,
    })

  const seguirCuenta = (fila: UsageClientRow) =>
    setObjetivo({
      clientId: fila.clientId,
      clientNombre: fila.clientNombre,
      sugerencia: `Entender la caída de consumo de ${fila.clientNombre}`,
      contexto:
        fila.variacionCreditos === null
          ? `El consumo de ${fila.clientNombre} en ${formatPeriodo(payload.periodo)} no tiene mes anterior comparable.`
          : `${fila.clientNombre} consumió ${formatMetricValue(fila.variacionCreditos.actual, 'count')} créditos en ${formatPeriodo(payload.periodo)} contra ${formatMetricValue(fila.variacionCreditos.base.valor, 'count')} en ${formatPeriodo(fila.variacionCreditos.base.periodo)}${
              fila.variacionCreditos.pct === null
                ? '.'
                : ` (${formatMetricValue(fila.variacionCreditos.pct, 'percent')}).`
            } La caída de consumo se ve semanas antes que el aviso de baja.`,
    })

  const seguirExpansion = (fila: UsageClientRow) =>
    setObjetivo({
      clientId: fila.clientId,
      clientNombre: fila.clientNombre,
      sugerencia: `Proponer expansión a ${fila.clientNombre}`,
      contexto: `${fila.clientNombre} tiene ${formatMetricValue(fila.agentes.activos, 'count')} de ${formatMetricValue(fila.agentes.contratados, 'count')} empleados digitales contratados produciendo, y su consumo creció contra el mes anterior.`,
    })

  const seguirCreditos = (cuenta: CreditBalance) =>
    setObjetivo({
      clientId: cuenta.clientId,
      clientNombre: cuenta.clientNombre,
      sugerencia: `Renovar créditos de ${cuenta.clientNombre}`,
      contexto:
        cuenta.proyeccion === null
          ? `${cuenta.clientNombre} no tiene proyección de agotamiento: falta el saldo o no registra quema.`
          : `A ${cuenta.clientNombre} le quedan ${formatMetricValue(cuenta.proyeccion.base.saldo, 'count')} créditos y quema ${formatMetricValue(cuenta.proyeccion.base.quemaMensual, 'count')} por mes: se agota el ${cuenta.proyeccion.fecha}, en ${formatMetricValue(cuenta.proyeccion.dias, 'count')} ${plural(cuenta.proyeccion.dias, 'día', 'días')}.`,
    })

  /*
   * La acción de un KPI sin dato **no es cargarlo a mano**. Estos cuatro números
   * los mide Langfuse y sus slugs no están en el catálogo de métricas: un diálogo
   * de carga le pediría un número al usuario y la API lo rechazaría. Lo que sí
   * destraba el dato es la configuración de la fuente, que vive en Admin. Para
   * ops y comercial no se ofrece botón —`/admin` los dejaría en acceso denegado—
   * y el detalle de la tarjeta explica qué falta para que puedan pedirlo.
   */
  const pedirDato = esAdmin ? () => navegar('/admin') : undefined

  const conteosTriage: readonly TriageConteo[] = [
    {
      severidad: 'critical',
      cantidad: conteos.cuentasEnCaida,
      label: plural(conteos.cuentasEnCaida, 'cuenta en caída', 'cuentas en caída'),
      onClick: () => irA(CAIDA),
    },
    {
      severidad: 'critical',
      cantidad: conteos.agentesSinUso,
      label: plural(
        conteos.agentesSinUso,
        'empleado digital instalado sin uso',
        'empleados digitales instalados sin uso',
      ),
      onClick: () => irA(SIN_USO),
    },
    {
      severidad: 'warning',
      cantidad: conteos.cuentasPorAgotar,
      label: plural(
        conteos.cuentasPorAgotar,
        'cuenta por quedarse sin créditos',
        'cuentas por quedarse sin créditos',
      ),
      onClick: () => irA(CREDITOS),
    },
    {
      severidad: 'unknown',
      cantidad: conteos.agentesSinMedicion,
      label: plural(
        conteos.agentesSinMedicion,
        'empleado digital sin medición',
        'empleados digitales sin medición',
      ),
      onClick: () => irA(SIN_MEDICION),
    },
    {
      severidad: 'unknown',
      cantidad: conteos.cuentasSinMedicion,
      label: plural(
        conteos.cuentasSinMedicion,
        'cuenta sin medición',
        'cuentas sin medición',
      ),
    },
    {
      severidad: 'ok',
      cantidad: conteos.cuentasSanas,
      label: plural(
        conteos.cuentasSanas,
        'cuenta consumiendo sin sobresaltos',
        'cuentas consumiendo sin sobresaltos',
      ),
    },
  ]

  return (
    <Stack gap="lg">
      <Section
        titulo="Cómo se está usando el producto"
        bajada={
          <>
            Lo que Revenue y Clientes no dicen: si lo que se vendió e instaló está{' '}
            <b>corriendo</b>. Todo en {formatPeriodo(payload.periodo)}, medido por
            Langfuse y atribuido a cada cuenta con la plataforma.
          </>
        }
        regla={false}
        espaciado="sm"
      >
        {payload.hero.length === 0 ? (
          <Vacio
            icono={null}
            punteada
            mensaje="El período no trajo ninguna tarjeta de consumo"
            detalle="Las cuatro salen del consumo medido: sin una corrida del adapter no hay números para la fila."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {payload.hero.map((kpi) => (
              <UsageKpiCard
                key={kpi.slug}
                kpi={kpi}
                /*
                 * Solo los créditos llevan dirección. Son la unidad de
                 * facturación, así que consumir más es mejor sin ambigüedad.
                 *
                 * El **costo de servir va en neutro a propósito**: bajó 14% este
                 * mes porque el consumo bajó 15%, así que pintarlo de verde por
                 * "lower_better" celebraría un mes malo. Y "cuentas en caída" y
                 * "agentes activos" no traen variación, así que la dirección no
                 * cambiaría nada.
                 */
                {...(kpi.slug === 'usage_creditos_consumidos'
                  ? { direccion: 'higher_better' as const }
                  : {})}
                {...(pedirDato === undefined ? {} : { onPedirDato: pedirDato })}
                now={now}
              />
            ))}
          </div>
        )}
      </Section>

      <TriageBar
        estado={veredicto.estado}
        titular={veredicto.titular}
        conteos={conteosTriage}
        meta={`umbral de caída ${formatMetricValue(payload.umbralCaidaPct, 'percent')} · autonomía mínima ${formatMetricValue(payload.umbralAgotamientoMeses, 'months')}`}
        acciones={
          <>
            <Button variante="primario" onClick={() => irA(SIN_USO)}>
              Poner a producir lo instalado (
              {formatMetricValue(conteos.agentesSinUso, 'count')}{' '}
              {plural(conteos.agentesSinUso, 'agente', 'agentes')})
            </Button>
            <Button variante="secundario" onClick={() => irA(CADENA)}>
              Ver dónde se pierde el valor
            </Button>
          </>
        }
        className="rounded-card border border-border"
      />

      {/*
        La cadena va sola y arriba de la zona 1, no dentro: es el aporte central
        del módulo y lo que explica por qué existen los bloques que siguen. Un
        agente desplegado con cero trazas figuraba como éxito en la brecha de
        activación de Clientes, y acá se ve el tramo donde se pierde el valor.
      */}
      <Section
        id={CADENA}
        titulo="Dónde se pierde el valor entre contratar y usar"
        bajada="Tres tramos sobre la misma base. La brecha de instalación ya la medía Clientes; la de uso es la que este módulo agrega."
        espaciado="sm"
      >
        <CadenaBlock
          cadena={payload.cadena}
          conCeroTrazas={conteos.agentesSinUso}
          sinMedicion={conteos.agentesSinMedicion}
        />
      </Section>

      <Section
        id={ZONA_1}
        kicker="Zona 1"
        titulo="Lo que está roto"
        bajada={
          <>
            <b>{formatMetricValue(conteos.agentesSinUso, 'count')}</b>{' '}
            {plural(
              conteos.agentesSinUso,
              'empleado digital está instalado y no produce',
              'empleados digitales están instalados y no producen',
            )}
            , <b>{formatMetricValue(conteos.cuentasEnCaida, 'count')}</b>{' '}
            {plural(conteos.cuentasEnCaida, 'cuenta', 'cuentas')} se{' '}
            {plural(conteos.cuentasEnCaida, 'desplomó', 'desplomaron')} contra el
            mes anterior y{' '}
            <b>{formatMetricValue(conteos.cuentasPorAgotar, 'count')}</b>{' '}
            {plural(conteos.cuentasPorAgotar, 'se queda', 'se quedan')} sin
            créditos en semanas.
          </>
        }
      >
        <Stack gap="md">
          <AgentesBlock
            id={SIN_USO}
            variante="sin-uso"
            agentes={payload.agentesSinUso}
            links={payload.links}
            onCrearSeguimiento={seguirAgente}
          />

          <CuentasEnCaida
            id={CAIDA}
            filas={payload.cuentasEnCaida}
            links={payload.links}
            umbralCaidaPct={payload.umbralCaidaPct}
            onCrearSeguimiento={seguirCuenta}
            now={now}
          />

          <CreditosBlock
            id={CREDITOS}
            cuentas={payload.cuentasPorAgotarCreditos}
            links={payload.links}
            umbralAgotamientoMeses={payload.umbralAgotamientoMeses}
            esAdmin={esAdmin}
            onCrearSeguimiento={seguirCreditos}
            now={now}
          />

          <div id={SIN_MEDICION} className="min-w-0">
            <AgentesBlock
              variante="sin-medicion"
              agentes={payload.agentesSinMedicion}
              links={payload.links}
              onCrearSeguimiento={seguirAgente}
            />
          </div>
        </Stack>
      </Section>

      <Section
        kicker="Zona 2"
        titulo="Lo que se está moviendo"
        bajada="El consumo mes a mes, quién lo hace, con qué modelos y quién está listo para expandir."
      >
        <Stack gap="md">
          <ConsumoMensual
            serie={payload.consumoMensual}
            periodo={payload.periodo}
          />

          <TablaCuentas
            variante="consumo"
            filas={payload.porCliente}
            links={payload.links}
            umbralCaidaPct={payload.umbralCaidaPct}
            titulo="Consumo por cuenta"
            subtitulo="El desglose del total del período, cuenta por cuenta, con sus empleados digitales activos."
            vacio="Ninguna cuenta tiene consumo en el período."
            vacioDetalle="Sin una corrida del adapter de Langfuse no hay filas: no es que las cuentas no consumieron."
            now={now}
          />

          <PorModelo modelos={payload.porModelo} links={payload.links} />

          <TablaCuentas
            variante="expansion"
            filas={payload.senalesDeExpansion}
            links={payload.links}
            umbralCaidaPct={payload.umbralCaidaPct}
            titulo="Señales de expansión"
            subtitulo="Cuentas cuyo consumo creció contra el mes anterior. La palanca que el panel identificó como subutilizada."
            vacio="Ninguna cuenta creció su consumo contra el mes anterior."
            vacioDetalle="Entran acá las cuentas con dos meses medidos y variación positiva."
            footer="Crecer el consumo no es todavía una venta: es la conversación que se puede abrir con un número atrás."
            onCrearSeguimiento={seguirExpansion}
            now={now}
          />

          {/*
            El bloque de plata, y el único que no ven los tres roles. Solo se
            monta en la rama de admin del payload: para ops y comercial el campo
            ni viaja, así que no hay nada que esconder acá.
          */}
          {payload.rol === 'admin' ? (
            <EconomiaBlock filas={payload.economiaPorCuenta} />
          ) : null}
        </Stack>
      </Section>

      <Section
        kicker="Zona 3"
        titulo="Lo que está sano"
        bajada="Colapsado: una cuenta que consume estable y con saldo holgado no pide nada hoy."
        regla={false}
        espaciado="sm"
      >
        <CuentasSanas
          sanas={payload.cuentasSanas}
          umbralCaidaPct={payload.umbralCaidaPct}
          umbralAgotamientoMeses={payload.umbralAgotamientoMeses}
          sinMedicion={conteos.cuentasSinMedicion}
        />
      </Section>

      {objetivo !== null ? (
        <CrearSeguimientoDialog
          open
          onClose={() => setObjetivo(null)}
          entityType="client"
          entityId={objetivo.clientId}
          tituloInicial={objetivo.sugerencia}
          contexto={objetivo.contexto}
          nombre={objetivo.clientNombre}
          contextoEditable
        />
      ) : null}
    </Stack>
  )
}
